import React from 'react';

import { css } from '@emotion/css';
import { Card, Theme, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/button/Button';
import { useStyles } from '@/hooks/useStyles';
import { userFormattedName } from '@/models/UserModel.ts';
import { ValidateOrRejectIdentity } from '@/models/identity/FrenchIdentity';
import { IdentityValidation } from '@/models/identity/IdentityValidationModel';
import { toDateTime } from '@/utils/dateUtils.ts';

export type ValidatePatientCardProps = {
  identity: IdentityValidation;
  submitting: boolean;
  validateOrReject: (action: ValidateOrRejectIdentity) => void;
};

export const ValidatePatientCard: React.FC<ValidatePatientCardProps> = ({
  identity,
  submitting,
  validateOrReject,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const birthLocation = identity.franceBirthLocation
    ? `${
        identity.franceBirthLocation?.town
      } (${identity.franceBirthLocation?.post_code.slice(0, 2)})`
    : identity.franceBirthCountry;

  return (
    <Card elevation={2} className={styles.item}>
      <Typography variant="subtitle" className={styles.itemTitle}>
        {userFormattedName(identity.familyName, identity.givenName)}
      </Typography>
      <table>
        <tbody>
          <PatientLine
            labelClassname={styles.lineLabel}
            label={t('profile.identityFirstnames')}
            value={identity.identityFirstnames.join(', ')}
          />
          <PatientLine
            labelClassname={styles.lineLabel}
            label={t('profile.identityLastname')}
            value={identity.identityLastname}
          />
          <PatientLine
            labelClassname={styles.lineLabel}
            label={t('profile.gender')}
            value={
              identity.gender === 'male'
                ? t('profile.male')
                : t('profile.female')
            }
          />
          <PatientLine
            labelClassname={styles.lineLabel}
            label={t('profile.birthDate')}
            value={toDateTime(identity.birthDate).toLocaleString(
              DateTime.DATE_SHORT,
            )}
          />
          {birthLocation ? (
            <PatientLine
              labelClassname={styles.lineLabel}
              label={t('profile.birthPlace')}
              value={birthLocation}
            />
          ) : null}
        </tbody>
      </table>
      <div className={styles.actions}>
        <Button
          id={`identity-reject-${identity.id}`}
          data-action="identity-reject"
          status={submitting ? 'pending' : 'idle'}
          buttonType="critical"
          onClick={() => validateOrReject('reject')}
        >
          {t('button.reject')}
        </Button>
        <Button
          id={`identity-validate-${identity.id}`}
          data-action="identity-validate"
          status={submitting ? 'pending' : 'idle'}
          buttonType="primary"
          onClick={() => validateOrReject('validate')}
        >
          {t('button.accept')}
        </Button>
      </div>
    </Card>
  );
};

export type PatientLineProps = {
  label: string;
  value: string;
  labelClassname?: string;
};
const PatientLine: React.FC<PatientLineProps> = ({
  label,
  value,
  labelClassname,
}) => {
  return (
    <tr>
      <td className={labelClassname}>
        <Typography variant="body">{label}</Typography>
      </td>
      <td>
        <Typography variant="body">{value}</Typography>
      </td>
    </tr>
  );
};

const makeStyles = (theme: Theme) => ({
  item: css`
    padding: ${theme.spacing(12)};
    margin-bottom: ${theme.spacing(8)};
  `,
  itemTitle: css`
    padding-bottom: ${theme.spacing(8)};
  `,
  lineLabel: css`
    padding-right: ${theme.spacing(12)};
  `,
  list: css`
    padding: ${theme.spacing(8)} ${theme.spacing(2)};
    gap: ${theme.spacing(8)};
  `,
  actions: css`
    display: flex;
    flex-direction: row;
    padding-top: ${theme.spacing(12)};
    gap: ${theme.spacing(12)};
    justify-content: flex-end;
  `,
  reject: css``,
});
